
import { Button } from "@/components/ui/button";
import { Session } from "@supabase/supabase-js";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Edit, Shield } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { AvatarUpload } from "@/components/profile/AvatarUpload";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";

interface ProfileHeaderProps {
  profile: {
    id?: string;
    username?: string | null;
    avatar_url?: string | null;
    role?: string;
    is_verified?: boolean;
    verification_date?: string;
    google_maps_rating?: number;
    google_maps_reviews_count?: number;
    trustpilot_rating?: number;
    trustpilot_reviews_count?: number;
  };
  session: Session | null;
  refetch?: () => Promise<void>;
}

export const ProfileHeader = ({ profile, session, refetch }: ProfileHeaderProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const isOwnProfile = session?.user?.id === profile?.id;
  const [isUpdating, setIsUpdating] = useState(false);

  const handleEditProfile = () => {
    navigate('/profile/edit');
  };

  const handleAvatarChange = async (newUrl: string) => {
    try {
      setIsUpdating(true);
      console.log('Avatar updated:', newUrl);
      
      // Show success toast
      toast({
        title: "Avatar Updated",
        description: "Your profile picture has been successfully updated.",
      });
      
      // Refresh profile data
      if (refetch) {
        await refetch();
      }
    } catch (error) {
      console.error("Error updating avatar:", error);
      toast({
        variant: "destructive",
        title: "Update Failed",
        description: "There was a problem updating your avatar.",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <motion.div 
      className="flex flex-col items-center space-y-6 p-6 bg-gradient-to-b from-white to-purple-50/30 rounded-lg shadow-sm border"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div className="relative">
        {isOwnProfile && profile?.id ? (
          <AvatarUpload 
            userId={profile.id} 
            url={profile.avatar_url}
            username={profile.username}
            onAvatarChange={handleAvatarChange}
            isUpdating={isUpdating}
          />
        ) : (
          <AvatarUpload 
            userId={profile?.id || ''} 
            url={profile.avatar_url}
            username={profile.username}
            isReadOnly={true}
          />
        )}
        
        {profile.is_verified && (
          <div className="absolute -top-2 -right-2 bg-white rounded-full p-1 shadow-sm">
            <Shield className="h-5 w-5 text-green-500" />
          </div>
        )}
      </div>
      
      <div className="text-center space-y-3 w-full max-w-md">
        <h2 className="text-2xl font-bold">{profile.username || 'Anonymous User'}</h2>
        
        {profile.role && (
          <div className="inline-flex items-center justify-center">
            <Badge className="text-sm px-3 py-1 rounded-full bg-primary/10 text-primary font-medium capitalize">
              {profile.role}
            </Badge>
          </div>
        )}
      </div>

      {isOwnProfile && (
        <Button 
          variant="outline" 
          onClick={handleEditProfile}
          className="mt-6 group transition-all hover:border-primary/50"
        >
          <Edit className="w-4 h-4 mr-2 group-hover:text-primary transition-colors" />
          Edit Profile
        </Button>
      )}
    </motion.div>
  );
}
